import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Card, CardContent, TextField, Typography,} from '@mui/material';
import {DragHandle as DragHandleIcon,} from '@mui/icons-material';

interface ResizableTextAreaProps {
    title: string;
    icon: React.ReactNode;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    minHeight?: number;
    maxHeight?: number;
    initialRows?: number;
}

export const ResizableTextArea: React.FC<ResizableTextAreaProps> = ({
                                                                        title,
                                                                        icon,
                                                                        value,
                                                                        onChange,
                                                                        placeholder = '',
                                                                        disabled = false,
                                                                        minHeight = 120,
                                                                        maxHeight = 600,
                                                                        initialRows = 4,
                                                                    }) => {
    const [height, setHeight] = useState<number>(minHeight);
    const [isDragging, setIsDragging] = useState(false);
    const [isAutoHeight, setIsAutoHeight] = useState(true);
    const textFieldRef = useRef<HTMLTextAreaElement>(null);
    const startY = useRef<number>(0);
    const startHeight = useRef<number>(0);

    // Auto-resize based on content
    const calculateAutoHeight = useCallback(() => {
        if (!textFieldRef.current || !isAutoHeight) return;

        const textarea = textFieldRef.current;
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const newHeight = Math.max(minHeight + 90, Math.min(maxHeight, scrollHeight + 60)); // Added +90 for minimum padding
        setHeight(newHeight);
    }, [minHeight, maxHeight, isAutoHeight]);

    // Recalculate height when content changes
    useEffect(() => {
        calculateAutoHeight();
    }, [value, calculateAutoHeight]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setIsDragging(true);
        setIsAutoHeight(false); // Disable auto-height when manually resizing
        startY.current = e.clientY;
        startHeight.current = height;

        const handleMouseMove = (event: MouseEvent) => {
            const deltaY = event.clientY - startY.current;
            const newHeight = Math.max(
                minHeight + 40, // Ensure minimum height with padding
                Math.min(maxHeight, startHeight.current + deltaY)
            );
            setHeight(newHeight);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
    }, [height, minHeight, maxHeight]);

    return (
        <Card
            variant='outlined'
            sx={{
                mb: 3,
                position: 'relative',
                transition: isDragging ? 'none' : 'height 0.2s ease',
                height: height + 10,
                minHeight: 'fit-content', // Ensure minimum height
                '&:hover .resize-handle': {
                    opacity: 0.7,
                }
            }}
        >
            <CardContent sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                <Box display='flex' alignItems='center' gap={1} mb={2}>
                    {icon}
                    <Typography variant='h6'>{title}</Typography>
                </Box>

                <TextField
                    inputRef={textFieldRef}
                    fullWidth
                    multiline
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    sx={{
                        flex: 1,
                        '& .MuiInputBase-root': {
                            height: '100%',
                            alignItems: 'flex-start',
                        },
                        '& .MuiInputBase-inputMultiline': {
                            resize: 'none',
                            overflow: 'auto',
                            height: `${height - 120}px !important`,
                        }
                    }}
                    InputProps={{
                        sx: {
                            height: '100%',
                            alignItems: 'flex-start',
                            '& textarea': {
                                height: '100% !important',
                                overflow: 'auto !important',
                            }
                        }
                    }}
                />
            </CardContent>

            {/* Resize Handle */}
            <Box
                className="resize-handle"
                onMouseDown={handleMouseDown}
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '12px',
                    cursor: 'ns-resize',
                    backgroundColor: isDragging ? 'primary.light' : 'action.hover',
                    opacity: 0,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                        backgroundColor: 'primary.light',
                        opacity: '1 !important',
                    }
                }}
            >
                <DragHandleIcon
                    sx={{
                        fontSize: '14px',
                        color: isDragging ? 'primary.main' : 'text.secondary',
                        transform: 'rotate(90deg)',
                    }}
                />
            </Box>

            {/* Visual feedback during dragging */}
            {isDragging && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'primary.main',
                        opacity: 0.03,
                        pointerEvents: 'none',
                        borderRadius: 1,
                    }}
                />
            )}
        </Card>
    );
};
